import {
  createFileRoute,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, type JSX } from 'react';

import { ensureQualityEnabled } from '@/features/quality/guard.js';
import { openFindingInAgent } from '@/features/quality/finding-actions/openFindingInAgent.js';
import { findingQueryOptions } from '@/lib/api/quality.js';

export const Route = createFileRoute('/quality_/findings/$id/chat')({
  component: FindingChatHandoffPage,
  beforeLoad: () => ensureQualityEnabled(),
});

/**
 * Deep-link handoff (cti-wrapper/docs/MEDA-416 AutoRecordedTestReplay.md "Frontends"): `/quality/findings/:id/chat`
 * fetches the finding with the user's JWT, then opens a new chat with a prefilled remediation draft
 * (the user sends it). Renders a brief loading state while resolving; on a fetch failure it still
 * hands off with a bare id so the agent can fetch the record itself. This route never shows chat UI —
 * it redirects to /chats.
 */
function FindingChatHandoffPage(): JSX.Element {
  const { id } = useParams({ from: '/quality_/findings/$id/chat' });
  const navigate = useNavigate();
  const { data: finding, isError } = useQuery(findingQueryOptions(id));

  useEffect(() => {
    if (finding) {
      openFindingInAgent({ finding, navigate });
      return;
    }
    if (isError) {
      // Couldn't load the finding (deleted / no access / transient) — hand off with a minimal record
      // so the chat still opens; the agent resolves the full finding by id via get_finding.
      openFindingInAgent({
        finding: {
          id,
          project_id: '',
          run_id: null,
          type: 'heal',
          severity: 'info',
          fingerprint: null,
          payload: {},
          review_status: 'unreviewed',
          created_at: '',
          updated_at: '',
          linked_finding_id: null,
        },
        navigate,
        label: 'Finding',
      });
    }
  }, [finding, isError, id, navigate]);

  return (
    <div className="flex h-full w-full items-center justify-center p-8 text-sm text-muted-foreground">
      Opening finding review…
    </div>
  );
}
